/**
 * Elite Supporter PREVIEWS ("Preview Release Access", perks.previewReleaseAccess): early access
 * to a whole game, a game's companion, or one feature, before everyone gets it.
 *
 * ONE list, `PREVIEWS`, holds every preview, active and past. It feeds the gates below AND the
 * public list on www.th.gl/previews (+ the support-me box, the FAQ entry, the feedback widget).
 *
 *  • kind "game"      — gates BOTH the web map/db pages AND the in-game companion. Non-Elite users
 *    get a "sign in / become Elite" page (PREVIEW_RELEASE_APPS).
 *  • kind "companion" — gates ONLY the in-game companion (live mode / overlay); the web map/db
 *    pages are OPEN to everyone (PREVIEW_RELEASE_COMPANION_APPS).
 *  • kind "feature"   — gates the ids in `gates` (PREVIEW_FEATURES): `usePreviewFeature(id)`.
 *
 * To make a preview public, set `publicSince` (never delete the entry: it stays on the public
 * list as a past preview), clean up the call sites, add a "now available to everyone"
 * release-notes line.
 *
 * The gate is CLIENT-SIDE on purpose: Elite status is resolved in the browser (userId cookie ->
 * /api/patreon fetch -> account store), so the server / middleware can't know it without replicating
 * that fetch on every request.
 *
 * A separate, per-account scope lives in games.ts: `companion.inviteOnly` (e.g. Pax Dei) — never
 * advertised, opened only for accounts whose server-resolved `invites` include the game
 * (`isCompanionAccessible`, admin UI at www.th.gl/admin/invites, gate = InviteOnlyGate).
 */
export type PreviewRelease = {
  /** Stable id: the feedback target (`preview:<slug>`) and the list anchor. */
  slug: string;
  kind: "game" | "companion" | "feature";
  /** games.ts id. A "game"/"companion" preview gates this id. Absent = every game. */
  game?: string;
  /** Player-facing name; also the upsell text "<title> is a Preview for Elite Supporters". */
  title: string;
  /** One plain sentence on what players get. Shown on the public list. */
  description: string;
  /** ISO date the preview started. */
  since: string;
  /** ISO date it went public. Absent = still in preview. */
  publicSince?: string;
  /** ISO date it is planned to go public (internal target, not shown). */
  plannedPublic?: string;
  /** Inbox item that tracks the preview and its go-public date. */
  inboxItem?: number;
  /** kind "feature": the PREVIEW_FEATURES ids it gates (live modes `live-mode:<mode>`,
   * filter values `previewFilterId()`). */
  gates?: string[];
};

/** Newest first. */
export const PREVIEWS: readonly PreviewRelease[] = [
  // History before 2026-10-12 rebuilt from the frontend git history (gate added/removed
  // commits) and the release notes. Older Discord-only previews (Companion App 2025-03,
  // Dragonwilds 2025-04, Grounded 2 2025-08) are left out: no confirmed public date.
  {
    slug: "dune-awakening-sandstorms",
    kind: "feature",
    game: "dune-awakening",
    title: "Live sandstorms",
    description:
      "Sandstorms show live on the Companion App map with their area and the direction they move, and you get a warning when you reach a storm's edge.",
    since: "2026-10-15",
    inboxItem: 113,
    gates: ["dune-awakening:sandstorms"],
  },
  {
    slug: "the-witcher-3-companion",
    kind: "companion",
    game: "the-witcher-3",
    title: "Companion App",
    description:
      "Your position and the direction you are facing show live on the map, and the map switches by itself when you travel between regions.",
    since: "2026-10-12",
  },
  {
    // THGLApp re-types placed items "<class>_PLOT". Three filters, one preview.
    slug: "palia-plot-items",
    kind: "feature",
    game: "palia",
    title: "Plot Items",
    description:
      "Everything placed on the housing plot you are on shows up live on the map: placed items, storage chests and crafting stations, each named after its item.",
    since: "2026-10-10",
    inboxItem: 154,
    gates: [
      "filter:palia:plot_item",
      "filter:palia:plot_storage",
      "filter:palia:plot_crafter",
    ],
  },
  {
    slug: "palworld-lucky-pals",
    kind: "feature",
    game: "palworld",
    title: "Lucky Pals",
    description: "Lucky Pals near you show up live on the map while you play.",
    since: "2026-10-06",
    publicSince: "2026-10-10",
    inboxItem: 111,
    gates: ["filter:palworld:lucky_pal"],
  },
  {
    slug: "baldurs-gate-ee-companion",
    kind: "companion",
    game: "baldurs-gate-ee",
    title: "Companion App",
    description:
      "Your party on the map and live enemy positions in the area you are in.",
    since: "2026-10-05",
    publicSince: "2026-10-10",
  },
  {
    slug: "discord-rich-presence",
    kind: "feature",
    title: "Discord Rich Presence",
    description:
      "Your Discord profile shows the game, the map and region you are in, your discovery progress and how long you have been playing.",
    since: "2026-10-05",
    publicSince: "2026-10-10",
    inboxItem: 444,
    gates: ["discord-presence"],
  },
  {
    slug: "palia-widgets-only-overlay",
    kind: "feature",
    game: "palia",
    title: "Widgets Only overlay",
    description:
      "Swap the overlay map for a small movable panel with your zone, grid square, the Palia time and your Weekly Wants progress.",
    since: "2026-10-04",
    publicSince: "2026-10-10",
    inboxItem: 169,
    gates: ["widgets-only-overlay"],
  },
  {
    slug: "where-winds-meet",
    kind: "game",
    game: "where-winds-meet",
    title: "Map and codex",
    description:
      "The Where Winds Meet map, codex and Companion App before the public release.",
    since: "2026-09-13",
    publicSince: "2026-09-28",
  },
  {
    slug: "sinking-city-2",
    kind: "game",
    game: "sinking-city-2",
    title: "Map and database",
    description:
      "The Sinking City 2 map and database before the public release.",
    since: "2026-09-13",
    publicSince: "2026-09-28",
  },
  {
    slug: "blood-of-dawnwalker",
    kind: "game",
    game: "blood-of-dawnwalker",
    title: "Map and codex",
    description:
      "The Blood of Dawnwalker map and codex before the public release.",
    since: "2026-09-13",
    publicSince: "2026-09-15",
  },
  {
    slug: "enshrouded-companion",
    kind: "companion",
    game: "enshrouded",
    title: "Companion App",
    description: "Live tracking on the Enshrouded map while you play.",
    since: "2026-09-01",
    publicSince: "2026-09-13",
  },
  {
    slug: "planet-crafter",
    kind: "game",
    game: "planet-crafter",
    title: "Map, database and Companion App",
    description:
      "The Planet Crafter map, database and Companion App before the public release.",
    since: "2026-08-11",
    publicSince: "2026-08-26",
  },
  {
    slug: "discover-all",
    kind: "feature",
    title: "Discover All",
    description:
      "Mark every marker of a filter or a whole filter group as discovered with one click.",
    since: "2026-07-31",
    publicSince: "2026-08-11",
    gates: ["discover-all"],
  },
  {
    slug: "dragonsword-awakening-save-import",
    kind: "feature",
    game: "dragonsword-awakening",
    title: "Save file import",
    description:
      "Upload your save file to mark the chests you have already opened, so the map only shows what is left to find.",
    since: "2026-07-30",
    publicSince: "2026-07-31",
    gates: ["dragonsword-awakening:save-import"],
  },
  {
    slug: "heartopia-companion",
    kind: "companion",
    game: "heartopia",
    title: "Companion App",
    description: "The Heartopia Companion App before the public release.",
    since: "2026-07-03",
    publicSince: "2026-07-07",
  },
  {
    slug: "combined-live-mode",
    kind: "feature",
    title: "Combined live mode",
    description: "Live and predicted markers on the map at the same time.",
    since: "2026-05-26",
    publicSince: "2026-06-06",
    gates: ["live-mode:combined"],
  },
  {
    slug: "marker-labels",
    kind: "feature",
    title: "Marker labels",
    description:
      "Marker names on the map: always, when you are in range, or while you hold a hotkey.",
    since: "2026-02-09",
    publicSince: "2026-02-19",
    gates: ["marker-labels"],
  },
  {
    slug: "audio-alerts",
    kind: "feature",
    title: "Audio alerts",
    description:
      "A sound plays when a tracked marker comes within range of you on the live map.",
    since: "2026-01-30",
    publicSince: "2026-02-09",
    gates: ["audio-alerts"],
  },
];

/** True while `preview` has not gone public. */
export function isActivePreview(preview: PreviewRelease): boolean {
  return !preview.publicSince;
}

const activeOfKind = (kind: PreviewRelease["kind"]) =>
  PREVIEWS.filter((p) => p.kind === kind && isActivePreview(p) && p.game).map(
    (p) => p.game as string,
  );

/** Games whose WEBSITE and IN-GAME COMPANION are Elite-only (kind "game"). */
export const PREVIEW_RELEASE_APPS: ReadonlySet<string> = new Set(
  activeOfKind("game"),
);

/** Games whose IN-GAME COMPANION is Elite-only, but whose WEBSITE is public. */
export const PREVIEW_RELEASE_COMPANION_APPS: ReadonlySet<string> = new Set(
  activeOfKind("companion"),
);

/** One gated feature id in PREVIEW_FEATURES. */
export type PreviewFeature = {
  /** Shown in upsell/lock texts: "<title> is a Preview for Elite Supporters". */
  title: string;
  /** ISO date the preview started. */
  since: string;
  /** ISO date it is planned to go public (a target, not a promise). */
  plannedPublic?: string;
  /** Inbox item that tracks the preview and its go-public date. */
  inboxItem?: number;
  /** The PREVIEWS entry it belongs to. */
  slug?: string;
};

/**
 * Feature gates of the ACTIVE previews, derived from PREVIEWS. Gate a feature with
 * `usePreviewFeature(id)` (hooks.ts) — or `isPreviewFeatureEnabled(id, hasPreviewAccess)` outside
 * React — and mark it with `<PreviewBadge>` (packages/ui). A public preview's ids are not here, so
 * every call site reports them as a normal feature (enabled, no badge, no lock).
 */
export const PREVIEW_FEATURES: Readonly<Record<string, PreviewFeature>> =
  Object.fromEntries(
    PREVIEWS.filter((p) => p.kind === "feature" && isActivePreview(p)).flatMap(
      (p) =>
        (p.gates ?? []).map((id) => [
          id,
          {
            title: p.title,
            since: p.since,
            plannedPublic: p.plannedPublic,
            inboxItem: p.inboxItem,
            slug: p.slug,
          },
        ]),
    ),
  );

/** Feedback target id of a preview (api-forge `/reports`, source `preview_feedback`). */
export function previewFeedbackTarget(slug: string): string {
  return `preview:${slug}`;
}

/**
 * Preview id of one filter value: `filter:<app>:<filterId>`. A live filter in
 * preview is shown with a Preview badge, can't be enabled without access, and
 * its types are not requested from the app (ActorTypeFilter).
 */
export function previewFilterId(appName: string, filterId: string): string {
  return `filter:${appName}:${filterId}`;
}

/**
 * The actor classes to request from the app: those whose filter value is
 * enabled, minus live filters in preview unless `previewAllowed`.
 */
export function selectRequestedActorTypes(
  appName: string,
  typesIdMap: Record<string, string>,
  enabledFilters: Iterable<string>,
  previewAllowed: boolean,
  registry: Readonly<Record<string, PreviewFeature>> = PREVIEW_FEATURES,
): string[] {
  const enabled = new Set(enabledFilters);
  return Object.keys(typesIdMap).filter(
    (key) =>
      enabled.has(typesIdMap[key]) &&
      (previewAllowed ||
        !isPreviewFeature(previewFilterId(appName, typesIdMap[key]), registry)),
  );
}

/** True while `id` is in preview (listed in PREVIEW_FEATURES). */
export function isPreviewFeature(
  id: string,
  registry: Readonly<Record<string, PreviewFeature>> = PREVIEW_FEATURES,
): boolean {
  return Object.prototype.hasOwnProperty.call(registry, id);
}

/**
 * Pure access check for non-React call sites: a preview feature needs the
 * Elite perk, everything else is open. React code uses `usePreviewFeature`,
 * which also handles SSR/hydration and the local-dev bypass.
 */
export function isPreviewFeatureEnabled(
  id: string,
  hasPreviewAccess: boolean,
  registry: Readonly<Record<string, PreviewFeature>> = PREVIEW_FEATURES,
): boolean {
  return hasPreviewAccess || !isPreviewFeature(id, registry);
}

/**
 * True if the WEB pages (map/db) are Elite-gated. Companion-only preview games are NOT gated here —
 * their website is open.
 */
export function isPreviewReleaseApp(
  appName: string | undefined | null,
): boolean {
  return !!appName && PREVIEW_RELEASE_APPS.has(appName);
}

/**
 * True if the IN-GAME COMPANION (live mode / overlay) is Elite-gated — either a full preview game
 * or a companion-only preview game. Used by the THGLApp paywall (app.tsx `isPreviewLocked`).
 */
export function isCompanionPreviewApp(
  appName: string | undefined | null,
): boolean {
  return (
    !!appName &&
    (PREVIEW_RELEASE_APPS.has(appName) ||
      PREVIEW_RELEASE_COMPANION_APPS.has(appName))
  );
}
